import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import dayjs from 'dayjs';

// financeiro-conciliacao-iugu Phase 5 (Task 6) -- puramente apresentacional (nenhum import de
// GraphQL/hook/services/), reusado pelos dois blocos de AC-14 (saldo, divergências): cada um
// exibe a idade do próprio dado (`consultadoEm`) e sua própria ação de atualizar, isoladamente.
// Mirror: OrigemConfirmacaoBadge.tsx (componente puramente apresentacional, sem service/hook).

export interface EstadoGatewayConciliacaoProps {
  consultadoEm: string | null;
  gatewayDisponivel: boolean;
  onAtualizar: () => void;
  atualizando?: boolean;
}

// Segundos de espera imposta após uma atualização. Existe porque cada clique aqui dispara uma
// varredura AO VIVO contra a Iugu (centenas de faturas, sem cache no Service) -- cliques repetidos
// enfileiram varreduras caras que ninguém pediu. Também dá ao operador um sinal de que a ação foi
// registrada, em vez de um botão que volta ao normal e parece não ter feito nada.
export const SEGUNDOS_DE_ESPERA_APOS_ATUALIZAR = 5;

const EstadoGatewayConciliacao: React.FC<EstadoGatewayConciliacaoProps> = ({
  consultadoEm,
  gatewayDisponivel,
  onAtualizar,
  atualizando = false,
}) => {
  const [bloqueadoAte, setBloqueadoAte] = useState<number | null>(null);
  const [segundosRestantes, setSegundosRestantes] = useState(0);

  // A contagem deriva de um PRAZO no relogio, nao de um setTimeout encadeado que subtrai 1 a cada
  // tick. Medido nesta tela: com as ~140 linhas da tabela renderizadas, o event loop fica tao
  // congestionado que cada tick de 1s levava ~1,5s de tempo real -- a versao encadeada acumulava
  // esse atraso e prendia o botao por bem mais que os 5s prometidos. Derivando de `bloqueadoAte`,
  // um tick atrasado apenas pula numeros: a espera termina na hora certa de qualquer jeito.
  useEffect(() => {
    if (bloqueadoAte === null) return undefined;
    const tick = () => {
      const falta = Math.max(0, Math.ceil((bloqueadoAte - Date.now()) / 1000));
      setSegundosRestantes(falta);
      if (falta === 0) setBloqueadoAte(null);
    };
    tick();
    const timer = setInterval(tick, 250);
    // Limpar no unmount evita atualizar estado de um componente desmontado -- a aba pode ser
    // trocada no meio da espera.
    return () => clearInterval(timer);
  }, [bloqueadoAte]);

  const idadeDoDado = consultadoEm
    ? `Dados de ${dayjs(consultadoEm).format('DD/MM/YYYY HH:mm')}`
    : 'Dados ainda não consultados';

  const emEspera = segundosRestantes > 0;
  const desabilitado = atualizando || emEspera;

  const handleAtualizar = () => {
    onAtualizar();
    setSegundosRestantes(SEGUNDOS_DE_ESPERA_APOS_ATUALIZAR);
    setBloqueadoAte(Date.now() + SEGUNDOS_DE_ESPERA_APOS_ATUALIZAR * 1000);
  };

  // Três rótulos distintos para três estados distintos: em andamento, esperando para poder de
  // novo, e pronto. Reusar o mesmo texto faria "está rodando" e "acabou, aguarde" parecerem a
  // mesma coisa.
  const rotulo = atualizando
    ? 'Atualizando...'
    : emEspera
      ? `Aguarde ${segundosRestantes}s`
      : 'Atualizar';

  return (
    <Box display="flex" alignItems="center" flexWrap="wrap" gap={2} mb={1}>
      <Typography variant="body2" color="text.secondary">
        {idadeDoDado}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        onClick={handleAtualizar}
        disabled={desabilitado}
        data-testid="conciliacao-atualizar"
        startIcon={
          atualizando ? (
            <CircularProgress size={14} color="inherit" data-testid="conciliacao-atualizando-spinner" />
          ) : undefined
        }
      >
        {rotulo}
      </Button>
      {!gatewayDisponivel && (
        <Alert severity="warning" sx={{ width: '100%' }}>
          Gateway da Iugu indisponível no momento — os dados exibidos podem estar desatualizados.
        </Alert>
      )}
    </Box>
  );
};

export default EstadoGatewayConciliacao;
